import { Box, Skeleton } from "@mui/material";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { buildFarmerRows } from "../corporate/FarmerTable";
import { usePerFarmerActions } from "../hooks/useInsightsDashboardQueries";
import StatTile from "./StatTile";

const STAGE_STYLES = {
  invited: { dot: "#a1a1aa", value: "#52525b" },
  onboarding: { dot: COLORS.bellflower[500], value: COLORS.bellflower[700] },
  actioning: { dot: COLORS.dahlia[500], value: COLORS.dahlia[700] },
} as const;

/**
 * Current-state breakdown of programme farmers across three mutually
 * exclusive stages: invited (not yet onboarded), onboarding (consent signed,
 * no action plan) and actioning (at least one committed action).
 * Renders nothing when the corporate user has no farmer-detail access.
 */
export default function FarmerJourneyFunnel() {
  const { data: farmersData, isLoading: farmersLoading } =
    useAccessibleFarmerProgrammes();
  const { data: actionsData, isLoading: actionsLoading } =
    usePerFarmerActions();

  if (farmersLoading || actionsLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={88}
        sx={{ borderRadius: `${BORDER_RADIUS.medium}px` }}
      />
    );
  }

  const farmers = farmersData ?? [];
  if (farmers.length === 0) return null;

  const rows = buildFarmerRows(
    farmers,
    actionsData?.data,
    undefined,
    undefined,
    null
  );

  const invited = rows.filter((r) => r.status === "invited").length;
  const onboarding = rows.filter((r) => r.status === "onboarding").length;
  const actioning = rows.filter((r) => r.status === "actioning").length;

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
      <StatTile
        label="Invited"
        value={String(invited)}
        tooltip="Invited to the programme but not yet signed up and given consent."
        dotColor={STAGE_STYLES.invited.dot}
        valueColor={STAGE_STYLES.invited.value}
        large
      />
      <StatTile
        label="Onboarding"
        value={String(onboarding)}
        tooltip="Signed up and gave consent, but have not agreed a farm action plan yet."
        dotColor={STAGE_STYLES.onboarding.dot}
        valueColor={STAGE_STYLES.onboarding.value}
        large
      />
      <StatTile
        label="Actioning"
        value={String(actioning)}
        tooltip="Have at least one committed action in their farm action plan."
        dotColor={STAGE_STYLES.actioning.dot}
        valueColor={STAGE_STYLES.actioning.value}
        large
      />
    </Box>
  );
}
